import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Checkbox, Input, Radio, Rate } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { ICareerExerciseField } from 'core';

import { RepeatGroupWidget } from './RepeatGroupWidget';
import { SkillsWidget } from './SkillsWidget';

export interface IExerciseFieldWidget {
  field: ICareerExerciseField;
  value: unknown;
  onChange: (value: unknown) => void;
}

/**
 * Один виджет анкеты по типу поля (ТЗ 0039).
 *
 * ⚠️ Осознанно НЕ переиспользует `QuestionWidget` из экрана опросов. Словари
 * пересекаются на четырёх типах из четырнадцати и расходятся на всём, что
 * важно: у опросов есть `nps` и вопросы про преподавателя и нет привязок, у
 * анкеты — повторяемые блоки, навыки, ссылки, даты и подсказки-предзаполнения.
 * Общий виджет стал бы свитчем по объединению с мёртвыми ветками с обеих
 * сторон, а рефакторинг тронул бы работающий, покрытый тестами экран опросов
 * ради нуля функциональности.
 */
export const ExerciseFieldWidget = memo(({ field, value, onChange }: IExerciseFieldWidget) => {
  const { t } = useTranslation();

  switch (field.fieldType) {
    case 'long_text':
      return (
        <Input.TextArea
          rows={4}
          maxLength={field.maxLength ?? undefined}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'yes_no':
      return (
        <Radio.Group value={Boolean(value)} onChange={(event) => onChange(event.target.value)}>
          <Radio value>{t('careerCenter.exercise.yes')}</Radio>
          <Radio value={false}>{t('careerCenter.exercise.no')}</Radio>
        </Radio.Group>
      );

    case 'rating':
      return (
        <Rate
          count={5}
          value={(value as number) ?? 0}
          onChange={(next) => onChange(next || null)}
        />
      );

    case 'single_choice':
      return (
        <Radio.Group value={value as number} onChange={(event) => onChange(event.target.value)}>
          {field.options.map((option, index) => (
            <RadioRow key={option}>
              <Radio value={index}>{option}</Radio>
            </RadioRow>
          ))}
        </Radio.Group>
      );

    case 'multi_choice':
      return (
        <Checkbox.Group value={(value as number[]) ?? []} onChange={(next) => onChange(next)}>
          {field.options.map((option, index) => (
            <RadioRow key={option}>
              <Checkbox value={index}>{option}</Checkbox>
            </RadioRow>
          ))}
        </Checkbox.Group>
      );

    case 'date':
      // Нативный input[type=date], а не antd DatePicker: тот тянет moment,
      // которого в зависимостях LMS нет, а формат на проводе всё равно
      // ГГГГ-ММ-ДД — ровно то, что нативное поле и отдаёт.
      return (
        <Input
          type="date"
          value={(value as string) ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'skills':
      return <SkillsWidget value={value} onChange={onChange} />;

    case 'repeat_group':
      return <RepeatGroupWidget binding={field.binding} value={value} onChange={onChange} />;

    case 'photo':
      // Фото загружается в конструкторе резюме: там уже есть работающая
      // multipart-загрузка с проверкой размера и типа. Дублировать её в
      // анкете значит завести второй путь загрузки файла — и второй набор
      // граблей. Честнее отправить студента туда, где это работает.
      return <Hint>{t('careerCenter.exercise.photoHint')}</Hint>;

    default:
      return (
        <Input
          type={field.fieldType === 'email' ? 'email' : 'text'}
          maxLength={field.maxLength ?? undefined}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
});

const RadioRow = styled.div`
  display: block;
  margin-bottom: 6px;
`;

const Hint = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;
